import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CreditCard, Check, Sparkles, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../lib/api';

/**
 * Stripe-backed Abonnement & Abrechnung section (mock by default). Extracted
 * verbatim from the legacy SettingsPage.jsx.
 */
export default function SubscriptionSection({ practice, className = '' }) {
  const [plans, setPlans] = useState([]);
  const [provider, setProvider] = useState('mock');
  const [trialDays, setTrialDays] = useState(14);
  const [vatRate, setVatRate] = useState(0.20);
  const [setupFee, setSetupFee] = useState(399);
  const [legalNote, setLegalNote] = useState('');
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [confirmFor, setConfirmFor] = useState(null);
  // OPT-IN: the customer must explicitly tick this box to be charged the
  // Einrichtungs-Support add-on. Default = false.
  const [setupRequested, setSetupRequested] = useState(false);
  // Conversion booster: subtle expandable list of what the support includes.
  // Collapsed by default — keep the checkbox uncluttered.
  const [setupDetailsOpen, setSetupDetailsOpen] = useState(false);

  const load = React.useCallback(async () => {
    const [plansRes, subRes] = await Promise.all([
      api.get('/api/stripe/plans'),
      api.get('/api/stripe/subscription'),
    ]);
    setPlans(plansRes.data.plans || []);
    setProvider(plansRes.data.provider || 'mock');
    setTrialDays(plansRes.data.trial_days || 14);
    setVatRate(plansRes.data.vat_rate ?? 0.20);
    setSetupFee(plansRes.data.setup_fee_eur ?? 399);
    setLegalNote(plansRes.data.legal_vat_note_de || '');
    setSub(subRes.data || null);
    if (subRes.data?.billing_cycle) setBillingCycle(subRes.data.billing_cycle);
  }, []);

  useEffect(() => {
    let pollTimer = null;
    (async () => {
      try {
        await load();
        const params = new URLSearchParams(window.location.search);
        const status = params.get('stripe');
        const sessionId = params.get('session_id');
        if (status === 'success' && sessionId) {
          let attempts = 0;
          const poll = async () => {
            attempts += 1;
            try {
              const r = await api.get(`/api/stripe/checkout/${sessionId}`);
              if (r.data.status === 'completed') {
                toast.success('Abonnement aktiv ✓');
                await load();
                window.history.replaceState({}, '', '/einstellungen');
                return;
              }
              if (r.data.status === 'expired' || attempts > 6) {
                toast.error('Zahlung nicht abgeschlossen');
                window.history.replaceState({}, '', '/einstellungen');
                return;
              }
              pollTimer = setTimeout(poll, 1500);
            } catch (e) { /* swallow */ }
          };
          poll();
        } else if (status === 'cancelled') {
          toast('Abbruch — kein Abonnement geändert.', { icon: 'ℹ️' });
          window.history.replaceState({}, '', '/einstellungen');
        } else if (status === 'portal_return') {
          await load();
          toast.success('Portal aktualisiert');
          window.history.replaceState({}, '', '/einstellungen');
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { if (pollTimer) clearTimeout(pollTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function askConfirm(planId) {
    try {
      // If the setup fee was already paid on a prior subscription, never
      // charge it again — and lock the checkbox UI accordingly via the
      // `setup_fee_paid_at` field downstream. Otherwise honour the checkbox.
      const alreadyPaid = !!sub?.setup_fee_paid_at;
      const includeSetup = !alreadyPaid && setupRequested;
      const setupParam = includeSetup ? '&setup=1' : '&setup=0';
      const r = await api.get(`/api/stripe/checkout/summary?plan=${planId}&billing_cycle=${billingCycle}${setupParam}`);
      setConfirmFor({ planId, summary: r.data, setupRequested: includeSetup });
    } catch (e) {
      toast.error('Preisübersicht konnte nicht geladen werden');
    }
  }
  async function startCheckout(planId, setupChosen) {
    setLoading(true);
    try {
      const r = await api.post('/api/stripe/checkout', {
        plan: planId,
        billing_cycle: billingCycle,
        setup_fee_requested: !!setupChosen,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Stripe-Fehler');
      setLoading(false);
    }
  }
  async function openPortal() {
    setLoading(true);
    try {
      const r = await api.post('/api/stripe/portal', {});
      window.location.href = r.data.url;
    } catch (e) {
      const code = e.response?.data?.error;
      const msg = e.response?.data?.message;
      // Demo / mock-id cases — display the friendly message returned by the
      // backend instead of a generic "Portal nicht verfügbar".
      if (code === 'demo_account' || code === 'mock_subscription_in_real_mode') {
        toast(msg || 'Demo-Account — Stripe Billing Portal nicht verfügbar.', { icon: 'ℹ️' });
      } else {
        toast.error(msg || 'Portal nicht verfügbar');
      }
      setLoading(false);
    }
  }

  // Demo accounts (is_demo:true) skip the Stripe portal entirely — show a
  // DEMO badge instead. We also short-circuit when STRIPE_PROVIDER=real and
  // the stored subscription ID is a mock leftover (is_mock_subscription).
  const isDemoAccount = !!sub?.is_demo;
  const isMockSubInRealMode = !!sub?.is_mock_subscription && provider === 'real';
  const hidePortalButton = isDemoAccount || isMockSubInRealMode;

  const activeStatuses = ['active', 'trialing', 'past_due'];
  const isActive = !!sub && activeStatuses.includes(sub.subscription_status) && !!sub.plan;
  const hasSubscription = isActive || !!sub?.stripe_subscription_id;
  const dateLocale = 'de-AT';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  const fmtEur = (n) => (n || 0).toLocaleString(dateLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={`so-card p-5 ${className}`} data-testid="subscription-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold flex items-center gap-2"><CreditCard size={14} className="text-accent"/>Abonnement &amp; Abrechnung</h2>
        {provider === 'mock' && (
          <span className="text-[10px] uppercase tracking-wider bg-warning/10 text-warning px-2 py-0.5 rounded-full font-mono">Mock-Modus</span>
        )}
      </div>

      {isActive && (
        <div className="rounded-md border border-success/30 bg-success/5 p-4 mb-4" data-testid="subscription-active">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider text-text-secondary">Aktueller Plan</p>
              <p className="text-lg font-semibold text-navy mt-1">
                {plans.find((p) => p.id === sub.plan)?.name || sub.plan}
                <span className="ml-2 text-sm font-normal text-text-secondary">
                  · {sub.monthly_amount_eur} € / Monat <span className="text-text-secondary/70">zzgl. MwSt.</span>
                </span>
                {sub.billing_cycle === 'annual' && (
                  <span className="ml-2 inline-block text-[10px] uppercase tracking-wider bg-success/10 text-success px-2 py-0.5 rounded-full font-medium align-middle" data-testid="active-annual-pill">Jährliche Bindung</span>
                )}
              </p>
              <div className="mt-2 flex items-center gap-3 text-xs text-text-secondary flex-wrap">
                <StatusChip status={sub.subscription_status}/>
                {sub.trial_end && new Date(sub.trial_end) > new Date() && (
                  <span>Test endet am <strong>{fmtDate(sub.trial_end)}</strong></span>
                )}
                {sub.current_period_end && (
                  <span>Nächste Abrechnung: <strong>{fmtDate(sub.current_period_end)}</strong></span>
                )}
                {sub.billing_cycle === 'annual' && sub.contract_end && (
                  <span data-testid="active-contract-end">Jährliche Bindung bis <strong>{fmtDate(sub.contract_end)}</strong></span>
                )}
                {sub.cancel_at_period_end && (
                  <span className="text-warning font-medium">Kündigung am Periodenende</span>
                )}
              </div>
            </div>
            {hidePortalButton ? (
              <span
                className="inline-flex items-center justify-center rounded-md text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                style={{ background: '#94A3B8', color: '#fff' }}
                data-testid={isDemoAccount ? 'demo-account-badge' : 'mock-account-badge'}
                title={isDemoAccount ? 'Demo-Account' : 'Mock-Abonnement im Live-Modus'}
              >
                {isDemoAccount ? 'Demo-Account' : 'Mock-Konto'}
              </span>
            ) : (
              <button onClick={openPortal} disabled={loading} className="so-btn so-btn-secondary text-xs" data-testid="open-portal-btn">
                <ExternalLink size={12}/>Plan verwalten
              </button>
            )}
          </div>
          {sub.usage && (
            <div className="mt-4 grid grid-cols-2 gap-3" data-testid="subscription-usage">
              <UsageBar label="Assistenten-Accounts" used={sub.usage.staff_used} limit={sub.usage.staff_limit} unit="Sitze" testid="usage-staff"/>
              <UsageBar label="WhatsApp/SMS (diesen Monat)" used={sub.usage.messages_used_this_month} limit={sub.usage.messages_limit} unit="Nachrichten" testid="usage-messages"/>
            </div>
          )}
        </div>
      )}

      {!hasSubscription && (
        <p className="text-sm text-text-secondary mb-4">
          Noch kein Abonnement aktiv. Wählen Sie einen Plan — die ersten <strong>{trialDays} Tage sind kostenlos</strong>, jederzeit kündbar.
        </p>
      )}

      {billingCycle === 'monthly' && plans.length === 2 && (
        <div className="rounded-md p-3 mb-3 flex items-center gap-3" style={{ background: '#16A34A', color: '#ffffff' }} data-testid="annual-saving-banner">
          <Sparkles size={16} className="shrink-0"/>
          <p className="text-sm font-medium leading-snug">
            Bei jährlicher Zahlung sparen Sie {plans.find(p => p.id === 'starter')?.annual_saving_eur} €/Jahr (Starter) oder {plans.find(p => p.id === 'pro')?.annual_saving_eur} €/Jahr (Pro) — 2 Monate gratis!
          </p>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap" data-testid="billing-interval-toggle">
        <p className="text-[11px] uppercase tracking-wider text-text-secondary">Abrechnungs­modell</p>
        <div className="inline-flex rounded-full bg-border/40 p-0.5 text-xs">
          <button type="button" onClick={() => setBillingCycle('monthly')} className={`px-3 py-1 rounded-full transition-colors ${billingCycle === 'monthly' ? 'bg-white text-navy shadow-sm font-medium' : 'text-text-secondary'}`} data-testid="interval-monthly">Monatlich</button>
          <button type="button" onClick={() => setBillingCycle('annual')} className={`px-3 py-1 rounded-full transition-colors flex items-center gap-1.5 ${billingCycle === 'annual' ? 'bg-white text-navy shadow-sm font-medium' : 'text-text-secondary'}`} data-testid="interval-annual">
            Jährlich
            <span className="text-[9px] uppercase tracking-wider bg-success/15 text-success px-1.5 py-0.5 rounded-full font-bold">Spare 2 Monate</span>
          </button>
        </div>
      </div>

      {/* OPTIONAL Einrichtungs-Support add-on — explicit opt-in. Hidden once
          the practice has already paid the fee on a previous subscription. */}
      {!sub?.setup_fee_paid_at && (
        <label
          htmlFor="setup-fee-opt-in"
          className={`flex items-start gap-3 rounded-md border p-3 mb-3 cursor-pointer transition-colors ${setupRequested ? 'border-accent bg-accent/5' : 'border-border bg-white hover:border-accent/50'}`}
          data-testid="setup-fee-optin"
        >
          <input
            id="setup-fee-opt-in"
            type="checkbox"
            checked={setupRequested}
            onChange={(e) => setSetupRequested(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border text-accent focus:ring-accent"
            data-testid="setup-fee-checkbox"
          />
          <div className="flex-1">
            <p className="text-sm font-medium text-navy">Einrichtungs-Support hinzufügen</p>
            <p className="text-xs text-text-secondary mt-0.5">
              Persönliche Einrichtung durch unser Team. Optional, einmalig.
            </p>
          </div>
          <p className="text-sm font-semibold text-navy tabular-nums whitespace-nowrap" data-testid="setup-fee-price">
            {setupFee} € <span className="text-xs font-normal text-text-secondary">einmalig zzgl. MwSt.</span>
          </p>
        </label>
      )}

      {/* Conversion booster — subtle green hint + collapsible bullet list.
          Sits below the checkbox but is independent of its state so users can
          peek at "what's included" before deciding. */}
      {!sub?.setup_fee_paid_at && (
        <div className="mb-4 -mt-1 pl-7 pr-1" data-testid="setup-fee-booster">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium" style={{ color: '#16A34A' }} data-testid="setup-fee-hint">
              Empfohlen für den ersten Start
            </span>
            <button
              type="button"
              onClick={() => setSetupDetailsOpen((v) => !v)}
              className="inline-flex items-center gap-0.5 text-text-secondary hover:text-navy transition-colors"
              data-testid="setup-fee-details-toggle"
              aria-expanded={setupDetailsOpen}
            >
              {setupDetailsOpen ? 'Details ausblenden' : 'Details anzeigen'}
              {setupDetailsOpen ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
            </button>
          </div>
          {setupDetailsOpen && (
            <ul className="mt-2 space-y-1 text-xs text-text-secondary" data-testid="setup-fee-details-list">
              {[
                'Twilio WhatsApp Konfiguration',
                'DSGVO-Einrichtung & AVV-Prüfung',
                'Branding & Praxis-Einstellungen',
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check size={12} className="mt-0.5 shrink-0" style={{ color: '#16A34A' }}/>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {plans.map((p) => {
          const isCurrent = isActive && sub.plan === p.id;
          const monthlyDisplay = billingCycle === 'annual' ? p.annual_monthly_amount_eur : p.monthly_amount_eur;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`rounded-md border p-4 flex flex-col ${
              isCurrent ? 'border-accent ring-2 ring-accent/30 bg-accent/5'
              : p.recommended ? 'border-accent/40 bg-white'
              : 'border-border bg-white'
            }`}>
              <div className="flex items-center justify-between mb-1 gap-1 flex-wrap">
                <p className="text-sm font-semibold">{p.name}</p>
                <div className="flex items-center gap-1">
                  {p.recommended && (
                    <span className="text-[10px] uppercase tracking-wider bg-accent/10 text-accent px-2 py-0.5 rounded-full font-medium flex items-center gap-1" data-testid={`plan-recommended-${p.id}`}>
                      <Sparkles size={10}/>Empfohlen
                    </span>
                  )}
                  {isCurrent && (
                    <span className="text-[10px] uppercase tracking-wider bg-success/10 text-success px-2 py-0.5 rounded-full font-medium">Aktiv</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-text-secondary mb-3 min-h-[32px]">{p.description}</p>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {monthlyDisplay}
                  <span className="text-sm font-normal text-text-secondary"> €/Monat</span>
                  <span className="ml-1 text-[10px] font-normal text-text-secondary/70">zzgl. MwSt.</span>
                </p>
                {billingCycle === 'annual' ? (
                  <p className="text-[11px] text-text-secondary mt-0.5" data-testid={`annual-summary-${p.id}`}>
                    Jährliche Bindung · monatliche Zahlung ·
                    <span className="ml-1 inline-block bg-success/10 text-success px-1.5 py-0.5 rounded-full font-semibold" data-testid={`annual-saving-${p.id}`}>
                      SPARE {p.annual_saving_eur} €/JAHR
                    </span>
                  </p>
                ) : (
                  <p className="text-[11px] text-text-secondary mt-0.5">
                    Monatlich · jederzeit kündbar — bei Jährlich-Auswahl nur {p.annual_monthly_amount_eur} €/Monat
                  </p>
                )}
              </div>
              <ul className="mt-3 space-y-1 text-xs text-text-secondary flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-1.5"><Check size={12} className="text-success mt-0.5 shrink-0"/><span>{f}</span></li>
                ))}
              </ul>
              {isCurrent ? (
                hidePortalButton ? (
                  <span
                    className="inline-flex items-center justify-center rounded-md text-[10px] uppercase tracking-wider font-bold mt-4 w-full py-2"
                    style={{ background: '#94A3B8', color: '#fff' }}
                    data-testid={isDemoAccount ? `demo-badge-${p.id}` : `mock-badge-${p.id}`}
                  >
                    {isDemoAccount ? 'Demo-Account' : 'Mock-Konto'}
                  </span>
                ) : (
                  <button onClick={openPortal} disabled={loading} className="so-btn so-btn-secondary text-xs mt-4 w-full" data-testid={`portal-${p.id}`}>Plan verwalten</button>
                )
              ) : (
                <button onClick={() => askConfirm(p.id)} disabled={loading} className={`so-btn ${p.recommended ? 'so-btn-primary' : 'so-btn-secondary'} text-xs mt-4 w-full`} data-testid={`checkout-${p.id}`}>
                  {hasSubscription ? 'Plan wechseln' : `${trialDays} Tage gratis testen`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-text-secondary leading-relaxed" data-testid="legal-vat-note">
        {legalNote || `Alle Preise verstehen sich als Nettopreise zzgl. der gesetzlichen Umsatzsteuer (${Math.round(vatRate * 100)} % MwSt.). Für Unternehmen mit gültiger UID-Nummer gilt das Reverse-Charge-Verfahren.`}
        <br/>Zahlung über Stripe · SCA-konform · Rechnungen + Zahlungsmethode jederzeit im Stripe-Portal verwalten.
      </p>

      {confirmFor && (
        <CheckoutConfirm
          plan={plans.find((p) => p.id === confirmFor.planId)}
          summary={confirmFor.summary}
          fmtEur={fmtEur}
          setupFee={setupFee}
          setupRequested={confirmFor.setupRequested}
          onConfirm={() => { startCheckout(confirmFor.planId, confirmFor.setupRequested); setConfirmFor(null); }}
          onCancel={() => setConfirmFor(null)}
        />
      )}
    </div>
  );
}

function CheckoutConfirm({ plan, summary, fmtEur, setupFee, setupRequested, onConfirm, onCancel }) {
  if (!plan || !summary) return null;
  const isAnnual = summary.billing_cycle === 'annual';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel} data-testid="checkout-confirm">
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-navy">{plan.name} ({isAnnual ? 'jährlich · mtl. verrechnet' : 'monatlich'})</h3>
        <p className="text-xs text-text-secondary mt-1">Bitte überprüfen Sie die Übersicht vor der Weiterleitung zu Stripe.</p>
        <div className="mt-4 space-y-1.5 text-sm" data-testid="confirm-breakdown">
          <Row label={`Plan ${isAnnual ? 'jährlich (mtl. verrechnet)' : 'monatlich'}`} value={`${fmtEur(summary.monthly_netto)} €${isAnnual ? ' × 12 Monate' : ''}`} testid="confirm-plan-line"/>
          {isAnnual && (<Row label="Jahres-Gesamtbetrag" value={`${fmtEur(summary.annual_total_netto)} € / Jahr`} muted/>)}
          {summary.setup_netto > 0 && (<Row label="Einrichtungs-Support (einmalig)" value={`${fmtEur(summary.setup_netto)} €`} testid="confirm-setup"/>)}
          <hr className="border-border my-2"/>
          <Row label={`MwSt. ${Math.round((summary.vat_rate || 0.20) * 100)} %`} value={`${fmtEur(summary.today_vat)} €`} testid="confirm-today-vat"/>
          <Row label="Heute gesamt" value={`${fmtEur(summary.today_brutto)} €`} bold testid="confirm-today-brutto"/>
          <hr className="border-border my-2"/>
          <Row label="Ab nächstem Monat" value={`${fmtEur(summary.monthly_brutto)} € inkl. MwSt.`} muted testid="confirm-next-month"/>
          {isAnnual && (<Row label="Vertragslaufzeit" value="12 Monate" muted testid="confirm-commitment"/>)}
        </div>
        <p className="mt-4 text-[10px] text-text-secondary leading-relaxed" data-testid="confirm-legal">
          {setupRequested
            ? `Sie haben den Einrichtungs-Support gewählt (${fmtEur(setupFee)} € netto, einmalig). Alle Preise zzgl. 20 % MwSt.`
            : 'Kein Einrichtungs-Support gewählt. Alle Preise zzgl. 20 % MwSt.'}
        </p>
        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="so-btn so-btn-secondary text-xs flex-1" data-testid="confirm-cancel">Abbrechen</button>
          <button onClick={onConfirm} className="so-btn so-btn-primary text-xs flex-1" data-testid="confirm-proceed">Weiter zu Stripe</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted, testid }) {
  return (
    <div className={`flex items-center justify-between ${bold ? 'font-semibold text-navy' : muted ? 'text-text-secondary' : ''}`} data-testid={testid}>
      <span>{label}</span><span className="tabular-nums">{value}</span>
    </div>
  );
}

function UsageBar({ label, used, limit, unit, testid }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color = pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-accent';
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-[11px] text-text-secondary mb-1">
        <span>{label}</span><span className="font-mono">{used} / {limit} {unit}</span>
      </div>
      <div className="h-1.5 bg-border rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active: { bg: 'bg-success/10', fg: 'text-success', label: 'Aktiv' },
    trialing: { bg: 'bg-accent/10', fg: 'text-accent', label: 'Testphase' },
    past_due: { bg: 'bg-warning/10', fg: 'text-warning', label: 'Zahlung überfällig' },
    canceled: { bg: 'bg-danger/10', fg: 'text-danger', label: 'Gekündigt' },
    incomplete: { bg: 'bg-border', fg: 'text-text-secondary', label: 'Unvollständig' },
  };
  const s = map[status] || { bg: 'bg-border', fg: 'text-text-secondary', label: status || '—' };
  return <span className={`${s.bg} ${s.fg} text-[11px] px-2 py-0.5 rounded-full font-medium`} data-testid={`sub-status-${status}`}>{s.label}</span>;
}
